import { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/api";
import { AnalysisParameter, CompanyAnalysisSettings } from "../types/api";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CogIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const AISettings = () => {
  const [parameters, setParameters] = useState<AnalysisParameter[]>([]);
  const [analysisSettings, setAnalysisSettings] =
    useState<CompanyAnalysisSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState<"success" | "no_success">(
    "success",
  );

  // Analysis settings edit state
  const [editingGeneralInstructions, setEditingGeneralInstructions] =
    useState(false);
  const [editingRedFlags, setEditingRedFlags] = useState(false);
  const [generalInstructionsText, setGeneralInstructionsText] = useState("");
  const [redFlagsText, setRedFlagsText] = useState("");

  // Add new parameter state
  const [showAddParameter, setShowAddParameter] = useState(false);
  const [newParameterName, setNewParameterName] = useState("");
  const [newParameterInstructions, setNewParameterInstructions] = useState("");
  const [newParameterPercentage, setNewParameterPercentage] = useState(10);

  // Edit parameter state
  const [editingParameterId, setEditingParameterId] = useState<string | null>(
    null,
  );
  const [editingParameterName, setEditingParameterName] = useState("");
  const [editingParameterInstructions, setEditingParameterInstructions] =
    useState("");
  const [editingParameterPercentage, setEditingParameterPercentage] =
    useState(10);

  // Limits state
  const [limits, setLimits] = useState<any>(null);

  const loadData = useCallback(async () => {
    try {
      await Promise.all([
        loadParameters(),
        loadAnalysisSettings(),
        loadLimits(),
      ]);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load AI settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadAnalysisSettings = async () => {
    try {
      const response = await apiService.getAnalysisSettings();
      if (response.success && response.data) {
        setAnalysisSettings(response.data);
        setGeneralInstructionsText(response.data.general_instructions || "");
        setRedFlagsText(response.data.red_flags_instructions || "");
      }
    } catch (error) {
      console.error("Failed to load analysis settings:", error);
      toast.error("Failed to load analysis settings");
    }
  };

  const loadParameters = async () => {
    try {
      const response = await apiService.getAnalysisParameters();
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          setParameters(response.data);
        } else {
          setParameters(response.data.parameters);
        }
      }
    } catch (error) {
      console.error("Failed to load parameters:", error);
      toast.error("Failed to load analysis parameters");
    }
  };

  const loadLimits = async () => {
    try {
      const response = await apiService.getCompanyLimits();
      if (response.success && response.data) {
        setLimits(response.data);
      }
    } catch (error) {
      console.error("Failed to load limits:", error);
      toast.error("Failed to load company limits");
    }
  };

  const handleUpdateGeneralInstructions = async () => {
    if (!generalInstructionsText.trim()) {
      toast.error("General instructions cannot be empty");
      return;
    }

    try {
      const response = await apiService.updateAnalysisSettings({
        generalInstructions: generalInstructionsText.trim(),
      });

      if (response.success && response.data) {
        setAnalysisSettings(response.data);
        setEditingGeneralInstructions(false);
        toast.success("General instructions updated successfully");
      } else {
        toast.error(
          response.message || "Failed to update general instructions",
        );
      }
    } catch (error: any) {
      console.error("Failed to update general instructions:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update general instructions",
      );
    }
  };

  const handleUpdateRedFlags = async () => {
    if (!redFlagsText.trim()) {
      toast.error("Red flags instructions cannot be empty");
      return;
    }

    try {
      const response = await apiService.updateAnalysisSettings({
        redFlagsInstructions: redFlagsText.trim(),
      });

      if (response.success && response.data) {
        setAnalysisSettings(response.data);
        setEditingRedFlags(false);
        toast.success("Red flags instructions updated successfully");
      } else {
        toast.error(
          response.message || "Failed to update red flags instructions",
        );
      }
    } catch (error: any) {
      console.error("Failed to update red flags instructions:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update red flags instructions",
      );
    }
  };

  const cancelGeneralInstructionsEdit = () => {
    setGeneralInstructionsText(analysisSettings?.general_instructions || "");
    setEditingGeneralInstructions(false);
  };

  const cancelRedFlagsEdit = () => {
    setRedFlagsText(analysisSettings?.red_flags_instructions || "");
    setEditingRedFlags(false);
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Calculate success scenario total
  const calculateSuccessPercentage = (
    parametersList: AnalysisParameter[] = parameters,
  ) => {
    return parametersList
      .filter((param) => param.is_enabled && param.applies_to === "success")
      .reduce(
        (total, param) =>
          total + parseFloat(param.percentage?.toString() || "0"),
        0,
      );
  };

  // Calculate no_success scenario total
  const calculateNoSuccessPercentage = (
    parametersList: AnalysisParameter[] = parameters,
  ) => {
    return parametersList
      .filter((param) => param.is_enabled && param.applies_to === "no_success")
      .reduce(
        (total, param) =>
          total + parseFloat(param.percentage?.toString() || "0"),
        0,
      );
  };

  const successTotal = calculateSuccessPercentage();
  const noSuccessTotal = calculateNoSuccessPercentage();

  // Get the active tab's total
  const getActiveTabTotal = () => {
    return activeTab === "success" ? successTotal : noSuccessTotal;
  };

  const totalPercentage = getActiveTabTotal();

  // Filter parameters based on active tab
  const getFilteredParameters = () => {
    return parameters.filter((param) => param.applies_to === activeTab);
  };

  const filteredParameters = getFilteredParameters();

  const handleToggleParameter = async (
    parameterId: string,
    isEnabled: boolean,
  ) => {
    try {
      const response = await apiService.toggleParameterStatus(
        parameterId,
        isEnabled,
      );
      if (response.success) {
        setParameters((params) =>
          params.map((p) =>
            p.id === parameterId ? { ...p, is_enabled: isEnabled } : p,
          ),
        );
        toast.success(
          `Parameter ${isEnabled ? "enabled" : "disabled"} successfully`,
        );
      } else {
        toast.error(response.message || "Failed to update parameter");
      }
    } catch (error: any) {
      console.error("Failed to toggle parameter:", error);
      toast.error(
        error.response?.data?.message || "Failed to update parameter",
      );
    }
  };

  const handleAddParameter = async () => {
    if (!newParameterName.trim() || !newParameterInstructions.trim()) {
      toast.error("Both parameter name and instructions are required");
      return;
    }

    if (newParameterPercentage < 0 || newParameterPercentage > 100) {
      toast.error("Percentage must be between 0 and 100");
      return;
    }

    if (
      limits &&
      filteredParameters.filter((p) => p.is_enabled).length >=
        limits.max_parameters_per_scenario
    ) {
      toast.error(
        `Maximum of ${limits.max_parameters_per_scenario} enabled parameters per scenario already reached`,
      );
      return;
    }

    // Check character limit
    if (
      limits &&
      newParameterInstructions.length > limits.max_parameter_instructions_chars
    ) {
      toast.error(
        `Parameter instructions exceed maximum length of ${limits.max_parameter_instructions_chars} characters`,
      );
      return;
    }

    try {
      const response = await apiService.addAnalysisParameter({
        parameterName: newParameterName.trim(),
        parameterInstructions: newParameterInstructions.trim(),
        percentage: newParameterPercentage,
        appliesTo: activeTab,
      });

      if (response.success && response.data) {
        setParameters([...parameters, response.data]);
        setNewParameterName("");
        setNewParameterInstructions("");
        setNewParameterPercentage(10);
        setShowAddParameter(false);
        toast.success("Analysis parameter added successfully");
      } else {
        toast.error(response.message || "Failed to add parameter");
      }
    } catch (error: any) {
      console.error("Failed to add parameter:", error);
      toast.error(error.response?.data?.message || "Failed to add parameter");
    }
  };

  const handleEditParameter = async (parameterId: string) => {
    if (!editingParameterName.trim() || !editingParameterInstructions.trim()) {
      toast.error("Both parameter name and instructions are required");
      return;
    }

    if (editingParameterPercentage < 0 || editingParameterPercentage > 100) {
      toast.error("Percentage must be between 0 and 100");
      return;
    }

    try {
      const response = await apiService.updateAnalysisParameter(parameterId, {
        parameterName: editingParameterName.trim(),
        parameterInstructions: editingParameterInstructions.trim(),
        percentage: editingParameterPercentage,
      });

      if (response.success && response.data) {
        setParameters((params) =>
          params.map((p) => (p.id === parameterId ? response.data! : p)),
        );
        setEditingParameterId(null);
        setEditingParameterName("");
        setEditingParameterInstructions("");
        setEditingParameterPercentage(10);
        toast.success("Analysis parameter updated successfully");
      } else {
        toast.error(response.message || "Failed to update parameter");
      }
    } catch (error: any) {
      console.error("Failed to update parameter:", error);
      toast.error(
        error.response?.data?.message || "Failed to update parameter",
      );
    }
  };

  const handleDeleteParameter = async (parameterId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this parameter? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const response = await apiService.deleteAnalysisParameter(parameterId);
      if (response.success) {
        setParameters((params) => params.filter((p) => p.id !== parameterId));
        toast.success("Analysis parameter deleted successfully");
      } else {
        toast.error(response.message || "Failed to delete parameter");
      }
    } catch (error: any) {
      console.error("Failed to delete parameter:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete parameter",
      );
    }
  };

  const startEditing = (parameter: AnalysisParameter) => {
    setEditingParameterId(parameter.id);
    setEditingParameterName(parameter.parameter_name);
    setEditingParameterInstructions(parameter.parameter_instructions);
    setEditingParameterPercentage(parameter.percentage);
  };

  const cancelEditing = () => {
    setEditingParameterId(null);
    setEditingParameterName("");
    setEditingParameterInstructions("");
    setEditingParameterPercentage(10);
  };

  const getPercentageStatusColor = () => {
    if (totalPercentage === 100) return "text-green-600";
    if (totalPercentage > 100) return "text-red-600";
    return "text-yellow-600";
  };

  const getPercentageStatusBg = () => {
    if (totalPercentage === 100) return "bg-green-50 border-green-200";
    if (totalPercentage > 100) return "bg-red-50 border-red-200";
    return "bg-yellow-50 border-yellow-200";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            AI Analysis Configuration
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure how AI analyzes your sales calls
          </p>
        </div>
      </div>

      {/* General Instructions Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  General Analysis Instructions
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Custom instructions for how AI should analyze calls
                </p>
              </div>
            </div>
            {!editingGeneralInstructions && (
              <button
                onClick={() => setEditingGeneralInstructions(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {editingGeneralInstructions ? (
            <div className="space-y-4">
              <textarea
                value={generalInstructionsText}
                onChange={(e) => setGeneralInstructionsText(e.target.value)}
                rows={6}
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                placeholder="Enter general instructions for AI analysis..."
                maxLength={limits?.max_general_instructions_chars}
              />
              {limits && (
                <div className="mt-1 flex justify-between text-xs">
                  <span
                    className={
                      generalInstructionsText.length >
                      limits.max_general_instructions_chars * 0.9
                        ? "text-orange-600"
                        : "text-gray-500"
                    }
                  >
                    {generalInstructionsText.length.toLocaleString()} /{" "}
                    {limits.max_general_instructions_chars.toLocaleString()}{" "}
                    characters
                  </span>
                  {generalInstructionsText.length >
                    limits.max_general_instructions_chars * 0.9 && (
                    <span className="text-orange-600">
                      {limits.max_general_instructions_chars -
                        generalInstructionsText.length}{" "}
                      remaining
                    </span>
                  )}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelGeneralInstructionsEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleUpdateGeneralInstructions}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {analysisSettings?.general_instructions ? (
                truncateText(analysisSettings.general_instructions, 300)
              ) : (
                <span className="text-gray-400 italic">
                  No general instructions set
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Red Flags Instructions Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Red Flags Definition
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Define what behaviors or statements should be flagged as
                  concerns
                </p>
              </div>
            </div>
            {!editingRedFlags && (
              <button
                onClick={() => setEditingRedFlags(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {editingRedFlags ? (
            <div className="space-y-4">
              <textarea
                value={redFlagsText}
                onChange={(e) => setRedFlagsText(e.target.value)}
                rows={6}
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                placeholder="Enter red flags definition..."
                maxLength={limits?.max_red_flags_instructions_chars}
              />
              {limits && (
                <div className="mt-1 flex justify-between text-xs">
                  <span
                    className={
                      redFlagsText.length >
                      limits.max_red_flags_instructions_chars * 0.9
                        ? "text-orange-600"
                        : "text-gray-500"
                    }
                  >
                    {redFlagsText.length.toLocaleString()} /{" "}
                    {limits.max_red_flags_instructions_chars.toLocaleString()}{" "}
                    characters
                  </span>
                  {redFlagsText.length >
                    limits.max_red_flags_instructions_chars * 0.9 && (
                    <span className="text-orange-600">
                      {limits.max_red_flags_instructions_chars -
                        redFlagsText.length}{" "}
                      remaining
                    </span>
                  )}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelRedFlagsEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleUpdateRedFlags}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {analysisSettings?.red_flags_instructions ? (
                truncateText(analysisSettings.red_flags_instructions, 300)
              ) : (
                <span className="text-gray-400 italic">
                  No red flags definition set
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab("success")}
              className={`${
                activeTab === "success"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } flex-1 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <div className="flex items-center justify-center space-x-2">
                <span>Successful Calls</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    successTotal === 100
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {successTotal.toFixed(0)}%
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("no_success")}
              className={`${
                activeTab === "no_success"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } flex-1 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <div className="flex items-center justify-center space-x-2">
                <span>Unsuccessful Calls</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    noSuccessTotal === 100
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {noSuccessTotal.toFixed(0)}%
                </span>
              </div>
            </button>
          </nav>
        </div>
        <div className="px-4 py-3 bg-gray-50">
          <p className="text-sm text-gray-600">
            {activeTab === "success"
              ? "These parameters are used when the call objective is achieved (sale made, appointment scheduled, etc.)"
              : "These parameters are used when the call objective is not achieved (rejection, no interest, etc.)"}
          </p>
        </div>
      </div>

      {/* Percentage Status */}
      <div className={`border rounded-lg p-4 ${getPercentageStatusBg()}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {totalPercentage === 100 ? (
              <CheckIcon className="h-6 w-6 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {activeTab === "success"
                  ? "Successful Calls"
                  : "Unsuccessful Calls"}{" "}
                Total Weight
              </h3>
              <p className="text-sm text-gray-600">
                Sum of all enabled parameter weights for{" "}
                {activeTab === "success" ? "successful" : "unsuccessful"} call
                scenarios
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${getPercentageStatusColor()}`}>
              {totalPercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500">
              {totalPercentage === 100
                ? "Perfect!"
                : totalPercentage > 100
                  ? "Over 100%"
                  : "Under 100%"}
            </div>
          </div>
        </div>

        {totalPercentage !== 100 && (
          <div className="mt-3 text-sm">
            {totalPercentage > 100 ? (
              <p className="text-red-800">
                <strong>Warning:</strong> Total percentage exceeds 100%. Please
                adjust the weights so they sum to exactly 100%.
              </p>
            ) : (
              <p className="text-yellow-800">
                <strong>Notice:</strong> Total percentage is under 100%. Please
                adjust the weights so they sum to exactly 100%.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Add New Parameter */}
      {showAddParameter && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Add New Parameter for{" "}
            {activeTab === "success" ? "Successful" : "Unsuccessful"} Calls
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parameter Name
              </label>
              <input
                type="text"
                value={newParameterName}
                onChange={(e) => {
                  // Remove spaces and only allow valid characters
                  const sanitized = e.target.value.replace(/\s+/g, "");
                  setNewParameterName(sanitized);
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                placeholder="e.g., ProductKnowledge or Product_Knowledge"
                pattern="^[a-zA-Z0-9_-]+$"
              />
              <p className="mt-1 text-xs text-gray-500">
                Use one word only (letters, numbers, underscores, or hyphens -
                no spaces)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weight Percentage (0-100%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={newParameterPercentage}
                onChange={(e) =>
                  setNewParameterPercentage(parseFloat(e.target.value) || 0)
                }
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                placeholder="10.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions for AI
              </label>
              <textarea
                value={newParameterInstructions}
                onChange={(e) => setNewParameterInstructions(e.target.value)}
                rows={3}
                className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                placeholder="Describe what the AI should analyze for this parameter..."
                maxLength={limits?.max_parameter_instructions_chars}
              />
              {limits && (
                <div className="mt-1 flex justify-between text-xs">
                  <span
                    className={
                      newParameterInstructions.length >
                      limits.max_parameter_instructions_chars * 0.9
                        ? "text-orange-600"
                        : "text-gray-500"
                    }
                  >
                    {newParameterInstructions.length.toLocaleString()} /{" "}
                    {limits.max_parameter_instructions_chars.toLocaleString()}{" "}
                    characters
                  </span>
                  {newParameterInstructions.length >
                    limits.max_parameter_instructions_chars * 0.9 && (
                    <span className="text-orange-600">
                      {limits.max_parameter_instructions_chars -
                        newParameterInstructions.length}{" "}
                      remaining
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddParameter(false);
                  setNewParameterName("");
                  setNewParameterInstructions("");
                  setNewParameterPercentage(10);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddParameter}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Parameter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parameters List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {activeTab === "success"
                  ? "Successful Call"
                  : "Unsuccessful Call"}{" "}
                Parameters
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {filteredParameters.length} parameter
                {filteredParameters.length !== 1 ? "s" : ""} configured
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div
                className={`text-right px-3 py-1 rounded-full text-sm font-medium ${
                  totalPercentage === 100
                    ? "bg-green-100 text-green-800"
                    : totalPercentage > 100
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                }`}
              >
                Total: {totalPercentage.toFixed(1)}%
              </div>
              <button
                onClick={() => setShowAddParameter(true)}
                disabled={
                  limits &&
                  filteredParameters.filter((p) => p.is_enabled).length >=
                    limits.max_parameters_per_scenario
                }
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  limits &&
                  filteredParameters.filter((p) => p.is_enabled).length >=
                    limits.max_parameters_per_scenario
                    ? `Maximum ${limits.max_parameters_per_scenario} enabled parameters per scenario`
                    : "Add new parameter"
                }
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Parameter
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {filteredParameters.length > 0 ? (
            <div className="space-y-4">
              {filteredParameters.map((parameter) => (
                <div
                  key={parameter.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  {editingParameterId === parameter.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parameter Name
                        </label>
                        <input
                          type="text"
                          value={editingParameterName}
                          onChange={(e) => {
                            // Remove spaces and only allow valid characters
                            const sanitized = e.target.value.replace(
                              /\s+/g,
                              "",
                            );
                            setEditingParameterName(sanitized);
                          }}
                          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                          pattern="^[a-zA-Z0-9_-]+$"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Use one word only (letters, numbers, underscores, or
                          hyphens - no spaces)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Weight Percentage (0-100%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={editingParameterPercentage}
                          onChange={(e) =>
                            setEditingParameterPercentage(
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Instructions for AI
                        </label>
                        <textarea
                          value={editingParameterInstructions}
                          onChange={(e) =>
                            setEditingParameterInstructions(e.target.value)
                          }
                          rows={3}
                          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2"
                          maxLength={limits?.max_parameter_instructions_chars}
                        />
                        {limits && (
                          <div className="mt-1 flex justify-between text-xs">
                            <span
                              className={
                                editingParameterInstructions.length >
                                limits.max_parameter_instructions_chars * 0.9
                                  ? "text-orange-600"
                                  : "text-gray-500"
                              }
                            >
                              {editingParameterInstructions.length.toLocaleString()}{" "}
                              /{" "}
                              {limits.max_parameter_instructions_chars.toLocaleString()}{" "}
                              characters
                            </span>
                            {editingParameterInstructions.length >
                              limits.max_parameter_instructions_chars * 0.9 && (
                              <span className="text-orange-600">
                                {limits.max_parameter_instructions_chars -
                                  editingParameterInstructions.length}{" "}
                                remaining
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={cancelEditing}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditParameter(parameter.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {parameter.parameter_name}
                          </h4>
                          <button
                            onClick={() =>
                              handleToggleParameter(
                                parameter.id,
                                !parameter.is_enabled,
                              )
                            }
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              parameter.is_enabled
                                ? "text-green-800 bg-green-100"
                                : "text-gray-800 bg-gray-100"
                            }`}
                          >
                            {parameter.is_enabled ? (
                              <>
                                <EyeIcon className="h-3 w-3 mr-1" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <EyeSlashIcon className="h-3 w-3 mr-1" />
                                Disabled
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">
                          {parameter.parameter_instructions}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created{" "}
                          {new Date(parameter.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3 ml-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                            parameter.is_enabled
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {parameter.percentage}%
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditing(parameter)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteParameter(parameter.id)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No{" "}
                {activeTab === "success"
                  ? "successful call"
                  : "unsuccessful call"}{" "}
                parameters
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first parameter for{" "}
                {activeTab === "success" ? "successful" : "unsuccessful"} calls.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => setShowAddParameter(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Parameter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CogIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Tips for Analysis Parameters
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Be specific about what you want the AI to evaluate</li>
                  <li>Focus on observable behaviors and measurable outcomes</li>
                  <li>Use clear, actionable language in your instructions</li>
                  {limits && (
                    <>
                      <li>
                        <strong>Character limits:</strong> General instructions
                        (
                        {limits.max_general_instructions_chars.toLocaleString()}{" "}
                        chars), Red flags (
                        {limits.max_red_flags_instructions_chars.toLocaleString()}{" "}
                        chars), Parameters (
                        {limits.max_parameter_instructions_chars.toLocaleString()}{" "}
                        chars)
                      </li>
                      <li>
                        <strong>Parameter limit:</strong> Maximum{" "}
                        {limits.max_parameters_per_scenario} parameters per
                        scenario
                      </li>
                    </>
                  )}
                  <li>
                    <strong>Each scenario must total exactly 100%</strong>{" "}
                    (Successful Calls and Unsuccessful Calls separately)
                  </li>
                  <li>
                    Only enabled parameters count toward the total percentage
                  </li>
                  <li>
                    Higher percentages mean more influence on the final score
                  </li>
                  <li>
                    Disabled parameters won't be included in call analysis
                  </li>
                  <li>
                    <strong>Successful call parameters</strong> focus on
                    closing, value delivery, and execution
                  </li>
                  <li>
                    <strong>Unsuccessful call parameters</strong> focus on
                    objection handling, recovery, and resilience
                  </li>
                  <li>
                    Changes apply to new call uploads, not existing analyses
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISettings;
